import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Select from "../../../components/select";
import { baseUrlWithAuth } from "../../../hooks/remote/apiClient";

const AuditLogs = () => {

  document.title = "Event Logs - CloudVantage";

  const userSessionData = useSelector((state) => state.user.userSessionData);

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all-logs");

  // Get student ID from session data
  const sessionProfile = userSessionData?.student_profile || userSessionData?.student || userSessionData;
  const studentId = sessionProfile?.uid || sessionProfile?.id;

  const options = [
    { value: "all-logs", label: "All event logs" },
    { value: "date", label: "Date" },
  ];

  useEffect(() => {
    let isMounted = true;

    const fetchActivities = async () => {
      try {
        setLoading(true);
        if (studentId) {
          // GET /api/v1/school/students/{student_id}/activities/
          const response = await baseUrlWithAuth.get(`/school/students/${studentId}/activities/`);
          if (isMounted) {
            const data = response.data?.results || response.data || [];
            setActivities(Array.isArray(data) ? data : []);
          }
        } else {
          // Fallback to standard audit logs
          const response = await baseUrlWithAuth.get('/users/audit-logs/');
          if (isMounted) {
            const data = response.data?.results || response.data || [];
            setActivities(Array.isArray(data) ? data : []);
          }
        }
      } catch (err) {
        console.error("Error fetching activities:", err);
        if (isMounted) {
          setActivities([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchActivities();

    return () => {
      isMounted = false;
    };
  }, [studentId]);

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
    setCurrentPage(1);
  };

  // Format the date for display
  const formatDate = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date)) return "N/A";
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();
    const weekday = date.toLocaleString('default', { weekday: 'long' });
    let suffix = "th";
    if (day === 1 || day === 21 || day === 31) suffix = "st";
    else if (day === 2 || day === 22) suffix = "nd";
    else if (day === 3 || day === 23) suffix = "rd";
    return `${weekday}, ${month} ${day}${suffix}, ${year}`;
  };

  // Format the time for display
  const formatTime = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date)) return "N/A";
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  // Map the activity list
  const auditLogListMap = Array.isArray(activities)
    ? activities.map((alog, index) => {
        const rawDate = alog.timestamp || alog.created;
        const timestamp = rawDate ? new Date(rawDate) : null;
        const date = (timestamp && !isNaN(timestamp)) ? formatDate(timestamp) : (rawDate || "N/A");
        const time = (timestamp && !isNaN(timestamp)) ? formatTime(timestamp) : "";
        const details = alog.description || alog.details || alog.activity_type || "No description provided";

        return {
          sn: index + 1,
          details,
          activityType: alog.activity_type || "SYSTEM",
          date,
          time,
          sortTimestamp: (timestamp && !isNaN(timestamp)) ? timestamp : new Date(0)
        };
      })
      .sort((a, b) => b.sortTimestamp - a.sortTimestamp)
      .map((item, index) => ({ ...item, sn: index + 1 }))
    : [];

  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;

  const filteredData = auditLogListMap.filter((row) => {
    const searchLower = searchText.toLowerCase();
    return (
      row.date.toLowerCase().includes(searchLower) ||
      row.time.toLowerCase().includes(searchLower) ||
      row.details.toLowerCase().includes(searchLower) ||
      row.activityType.toLowerCase().includes(searchLower)
    );
  });

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredData.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(filteredData.length / entriesPerPage);

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  return (
    <div className="min-h-screen px-4 md:px-10 pt-6 pb-10 dark:text-white">
      <p className="crimson-font text-lg font-medium text-primaryBlue">Event Logs</p>

      {loading ? (
        <div className="md:px-52 text-center grid text-gray-500 mt-32">
          <div>Loading event logs...</div>
        </div>
      ) : auditLogListMap?.length > 0 ? (
        <div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end my-10">
            <div className="md:col-span-3">
              <Select
                label="Filter logs"
                name="logs"
                options={options}
                value={selectedFilter}
                onChange={(e) => setSelectedFilter(e.target.value)}
              />
            </div>
            <div className="md:col-span-4">
              <input
                type="search"
                name="search"
                value={searchText}
                onChange={handleSearchChange}
                placeholder="Search by date, time, or details..."
                className="bg-white text-black w-full p-4 text-sm rounded-md border border-[#c4c4c460] active:outline-0 focus:outline-0 placeholder:opacity-70 placeholder:text-xs dark:bg-slate-800 dark:text-white"
              />
            </div>
          </div>

          <div className="overflow-x-auto rounded-md border dark:border-[#c4c4c432]">
            <table className="w-full text-sm text-left overflow-hidden">
              <thead className="bg-white dark:bg-slate-800 px-3 md:px-4 border text-gray-400 dark:border-[#c4c4c432] border-b text-sm font-semibold">
                <tr>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">
                    S/N
                  </th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">
                    Details
                  </th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">
                    Date
                  </th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">
                    Time
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-800 px-3 md:px-4 border rounded-b-md dark:border-[#c4c4c432] border-t-2 text-sm">
                {currentEntries.length > 0 ? (
                  currentEntries.map((row, index) => (
                    <tr
                      key={index}
                      className="dark:hover:bg-[#ffffff05] hover:bg-brandBlue/25 whitespace-nowrap"
                    >
                      <td className="text-start py-4 px-6">{row?.sn}.</td>
                      <td className="text-start py-4 px-6">{row?.details}</td>
                      <td className="text-start py-4 px-6">{row?.date}</td>
                      <td className="text-start py-4 px-6">{row?.time}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center py-10 text-gray-500">
                      No logs match your search criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="grid justify-center gap-4 md:flex md:justify-between items-center mt-4">
            <p className="text-gray-400 text-sm">
              Showing{" "}
              <span className="font-medium text-black dark:text-white">
                {indexOfFirstEntry + 1}
              </span>{" "}
              to{" "}
              <span className="font-medium text-black dark:text-white">
                {Math.min(indexOfLastEntry, filteredData.length)}
              </span>{" "}
              of{" "}
              <span className="font-medium text-black dark:text-white">
                {filteredData.length}
              </span>{" "}
              results
            </p>
            <div className="flex gap-3">
              <button
                className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${currentPage === 1 ? "opacity-50" : "opacity-100"}`}
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <button
                className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${currentPage === totalPages ? "opacity-50" : "opacity-100"}`}
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="md:px-52 text-center grid text-gray-500 mt-32">
          <div>
            It looks like you don't have any event logs yet. Once you perform activities in your learning space, your log details will appear here.
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
